// Utility functions for sell screen discount calculations and cart updates
import { productDataType, variantCombinationDetailsType } from "../../components/sellScreen/productDetailsModule";
import { overAllDiscount } from "../../components/sellScreen/sellScreenModule";
import { addCartProductType, CUSTOMER, CUSTOMER_TYPE, discountType, initialStateType, PaymentMethodType } from "../types/sellScreenTypes";
import { addCartProduct, ensureVariantDiscounts, getProductDetailsById } from "../slices/newSellscreenSlice";
import { ZenToast } from "zen-core-elements";
import { config } from "../../shared/environment";
import { AppDispatch } from "../store";
import CommonService from "../../shared/services/commonService";
import { normaliseProductAttributes } from "./discountEngine";

// Reads the store lazily: a static import would be circular (store -> newSellscreenSlice -> discountUtils).
// getState() throws while a reducer is executing, so reducers should pass currencySymbol explicitly.
const getCurrencySymbol = (): string => {
	try {
		return require("../store").store.getState()?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol ?? '₹'
	} catch {
		return '₹'
	}
}


export const PAYMENT_METHODS: PaymentMethodType[] = [
	{ id: 'Cash', icon: 'cash', color: '#10b981' },
	// { id: 'Gift Card', icon: 'gift', color: '#f59e0b' },
	{ id: 'Online Payment', icon: 'credit-card', color: '#3b82f6' },
	// { id: 'Credit Card', icon: 'credit-card', color: '#3b82f6' },
	// { id: 'Debit Card', icon: 'credit-card', color: '#14b8a6' },
	// { id: 'Square', icon: 'credit-card', color: '#6366f1' },
	// { id: 'Stripe', icon: 'credit-card', color: '#9333ea' },
	// { id: 'Store Credit', icon: 'store', color: '#f43f5e' },
	// { id: 'On Account', icon: 'account-clock', color: '#8b5cf6' },
	// { id: 'Other', icon: 'dots-horizontal', color: '#64748b' },
];

export function isValidAmount(value: string) {
	return /^(?:0(?:\.\d{0,2})?|[1-9]\d*(?:\.\d{0,2})?)$/.test(value);
}

export function getPaymentMode(value: PaymentMethodType["id"]) {
	switch (value) {
		case "Cash":
			return "CASH IN";
		case "Online Payment":
			return "ONLINE PAYMENT";
		case "Gift Card":
			return "GIFT CARD PAYMENT";
		case "Credit Card":
			return "ONLINE PAYMENT";
		case "Debit Card":
			return "ONLINE PAYMENT";
		case "Square":
			return "ONLINE PAYMENT";
		case "Stripe":
			return "ONLINE PAYMENT";
		case "Store Credit":
			return "STORE CREDIT PAYMENT";
		case "On Account":
			return "On Account";
		case "Other":
			return "OTHER PAYMENT";
		default:
			return "";
	}
}

export function isCustomerOnAccountEligible(customerDetails: any, payableAmount: number): boolean {
	if (!customerDetails || !customerDetails?.onAccountDetails) {
		return false;
	}
	const balance = Number(customerDetails?.onAccountDetails?.balance || 0);
	const limit = Number(customerDetails?.onAccountDetails?.limit || 0);
	const isEnabled = limit > 0 || balance > 0 || customerDetails?.isAllowOnAccount === true;

	if (!isEnabled) {
		return false;
	}

	if (payableAmount > balance) {
		return false;
	}

	return true;
}

// Validate manual discount applicability
export function validateManualDiscount({
	discount,
	cartTotal,
	cartQuantity,
	customerId,
	userUsageCount = 0,
	// propertyValues
}: {
	discount: overAllDiscount;
	cartTotal: number;
	cartQuantity: number;
	customerId: number | null;
	userUsageCount?: number;
	// propertyValues:PropertyValues[]
}) {
	const currentDate = new Date();
	let customerGroupIds: number[] = []
	let customerTypeId = customerId ? CUSTOMER_TYPE?.customer : CUSTOMER_TYPE?.guest

	// Customer eligibility
	// switch (discount.customerEligibilityType) {
	// 	case CUSTOMER.allCustomer:
	// 		// No restriction
	// 		break;
	// 	case CUSTOMER.customerType:
	// 		if (!discount.roleDiscountMappings?.length || !discount.roleDiscountMappings.includes(customerTypeId)) {
	// 			return { isApplicable: false, message: "This coupon is not valid for this customer type." };
	// 		}
	// 		break;
	// 	case CUSTOMER.customerGroup:
	// 		if (!discount.eligibility || !discount.eligibility.some((id) => customerGroupIds.includes(id))) {
	// 			return { isApplicable: false, message: "This coupon is not valid for your customer group." };
	// 		}
	// 		break;
	// 	case CUSTOMER.SpecificCustomer:
	// 		if (!discount.eligibility || !customerId || !discount.eligibility.includes(customerId)) {
	// 			return { isApplicable: false, message: "This coupon is not valid for this customer." };
	// 		}
	// 		break;
	// 	default:
	// 		return { isApplicable: false, message: "Invalid customer eligibility configuration." };
	// }

	// Date validation
	const startDate = discount.startDate ? new Date(discount.startDate) : null;
	const endDate = discount.endDate ? new Date(discount.endDate) : null;
	if (startDate && currentDate < startDate) {
		return { isApplicable: false, message: "This coupon is not active yet." };
	}
	if (endDate && currentDate > endDate) {
		return { isApplicable: false, message: "This coupon has expired." };
	}

	// Restriction based on amount / quantity
	if (discount.restrictionBasedOn && discount.minValue) {
		const minValue = Number(discount.minValue);
		const total = Math.abs(cartTotal);
		const quantity = Math.abs(cartQuantity);
		if (discount.restrictionBasedOn === 32 && total < minValue) {
			return { isApplicable: false, message: `This coupon applies only to purchases above ${CommonService.currencyStandard(getCurrencySymbol(), String(minValue))}.` };
		}
		if (discount.restrictionBasedOn === 33 && quantity < minValue) {
			return { isApplicable: false, message: `This coupon requires at least ${minValue} items.` };
		}
	}

	// User based usage limit
	if (discount.userBasedMaxUse != null && userUsageCount >= discount.userBasedMaxUse) {
		return { isApplicable: false, message: "You have reached the usage limit for this coupon." };
	}

	// Overall usage limit
	if (discount.overallUsageLimit != null && discount.overallUsageLimit <= 0) {
		return { isApplicable: false, message: "This coupon is no longer available." };
	}

	// Coupon is valid
	return { isApplicable: true, message: null };
}

// Calculate discount amount based on type and optional max amount
export function discountValueCalculation({
	discountTypeCode,
	discountValue,
	cartTotal,
	maxAmount = null,
}: {
	discountTypeCode: discountType;
	discountValue: number;
	cartTotal: number;
	maxAmount?: number | null;
}) {
	let discountAmount = 0;
	if (cartTotal < 0) {
		const absCart = Math.abs(cartTotal);
		const absVal = Math.abs(discountValue);
		if (discountTypeCode === "PERCENTAGE") {
			discountAmount = (absCart * absVal) / 100;
		} else {
			discountAmount = absVal;
		}
		if (maxAmount != null) {
			discountAmount = Math.min(discountAmount, Number(maxAmount));
		}
		discountAmount = Math.min(discountAmount, absCart);
		if (discountValue < 0) {
			discountAmount = -Math.abs(discountAmount);
		} else {
			discountAmount = -discountAmount;
		}
	} else {
		if (discountTypeCode === "PERCENTAGE") {
			discountAmount = (cartTotal * discountValue) / 100;
		} else {
			discountAmount = discountValue;
		}
		if (maxAmount != null && discountAmount > 0) {
			discountAmount = Math.min(discountAmount, Number(maxAmount));
		}
		if (cartTotal > 0 && discountAmount > 0) {
			discountAmount = Math.min(discountAmount, cartTotal);
		}
	}
	return Math.round(discountAmount * 100) / 100;
}
export function applyManualDiscount({
	state,
	locationId
}: {
	state: initialStateType;
	locationId: number;
}) {
	const { manualDiscount, cartTotal, cartQuantity } = state;
	if (manualDiscount[locationId]) {
		const validation = validateManualDiscount({
			discount: manualDiscount[locationId],
			cartTotal: cartTotal[locationId],
			cartQuantity: cartQuantity[locationId],
			customerId: state?.customerDetails?.id || null
		});
		if (validation.isApplicable) {
			const discountAmount = discountValueCalculation({
				discountTypeCode: manualDiscount?.[locationId]?.discountType?.discountTypeCode as discountType,
				discountValue: Number(manualDiscount?.[locationId]?.discountValue),
				cartTotal: cartTotal[locationId],
				maxAmount: manualDiscount?.[locationId]?.maxAmount ?? null,
			});
			state.manualDiscount[locationId] = { ...manualDiscount?.[locationId], isValidate: true, discountAmount };
			applyCartDiscountToProducts({ state, discountAmount, locationId });
		}
		else if (manualDiscount?.[locationId]?.isValidate) {
			state.manualDiscount[locationId] = { ...manualDiscount?.[locationId], isValidate: false, discountAmount: 0 };
			applyCartDiscountToProducts({ state, discountAmount: 0, locationId });
		} else {
			calculateSummary({ state, locationId })
		}
	}
}
// Apply a total discount amount proportionally across cart products
export function applyCartDiscountToProducts({
	state,
	discountAmount,
	locationId
}: {
	state: initialStateType;
	discountAmount: number;
	locationId: number;
}) {
	const cartProducts = state.cartProducts[locationId] || [];
	const cartTotal = state.cartTotal?.[locationId] || 0;
	if (!cartTotal || discountAmount === 0) {
		state.cartProducts[locationId] = cartProducts.map((item) => ({
			...item,
			discount: 0,
			finalPrice: item.price * item.quantity,
		}));
		calculateSummary({ state, locationId });
		return;
	}

	let distributed = 0;
	const absDiscountAmount = Math.abs(discountAmount);
	const absCartTotal = Math.abs(cartTotal);
	state.cartProducts[locationId] = cartProducts.map((item, index: number) => {
		const total = item.price * item.quantity;
		const absTotal = Math.abs(total);
		const discount =
			index === cartProducts.length - 1
				? Math.round((absDiscountAmount - distributed) * 100) / 100
				: Math.round((absDiscountAmount * absTotal) / absCartTotal * 100) / 100;
		distributed += discount;

		const autoDisc = item?.autoDiscount || 0;
		const appliedDiscount = autoDisc || discount;

		let finalPrice = total;
		if (total >= 0) {
			finalPrice = Math.round(Math.max(total - appliedDiscount, 0) * 100) / 100;
		} else {
			finalPrice = Math.round(Math.min(total + appliedDiscount, 0) * 100) / 100;
		}

		return {
			...item,
			discount,
			finalPrice,
		};
	});
	// Re‑calculate summary after discount distribution
	calculateSummary({ state, locationId });
}

// Update cart total and quantity based on current cartProducts
export function updateCartTotalAndQuantity({ state, locationId }: { state: initialStateType, locationId: number }) {
	const { quantity, total } = state.cartProducts?.[locationId]?.reduce(
		(sum, { price, quantity }) => {
			return {
				total: sum.total + price * quantity,
				quantity: sum.quantity + quantity,
			};
		},
		{ total: 0, quantity: 0 }
	) || { total: 0, quantity: 0 };
	if (!state.cartTotal) state.cartTotal = {};
	if (!state.cartQuantity) state.cartQuantity = {};
	state.cartTotal[locationId] = Math.round(total * 100) / 100;
	state.cartQuantity[locationId] = quantity;
}

// Calculate summary totals (subTotal, discounts, tax, grand total)
export function calculateSummary({ state, locationId }: { state: initialStateType, locationId: number }) {
	const { cartTotal, manualDiscount, additionalDiscount, additionalCharge, tip } = state;
	const { otherDiscount, tax } = (state.cartProducts[locationId] || []).reduce(
		(val, item) => {
			val.otherDiscount += item?.autoDiscount || 0;
			val.tax += item?.tax || 0;
			return val;
		},
		{ otherDiscount: 0, tax: 0 }
	);
	const couponDiscount = manualDiscount?.[locationId]?.isValidate ? manualDiscount?.[locationId]?.discountAmount || 0 : 0;
	const additionalDiscountAmount = additionalDiscount?.[locationId]?.discountAmount || 0;
	const discountedPrice = cartTotal[locationId] - (couponDiscount + additionalDiscountAmount + otherDiscount);
	const grandTotal = discountedPrice + (additionalCharge?.[locationId]?.amount || 0) + tax + (tip?.[locationId] || 0);
	state.summary[locationId] = {
		subTotal: cartTotal[locationId],
		otherDiscount,
		couponDiscount,
		additionalDiscount: additionalDiscountAmount,
		discountedPrice: Math.round(discountedPrice * 100) / 100,
		tax: Math.round(tax * 100) / 100,
		grandTotal: Math.round(grandTotal * 100) / 100,
	};
}

export const calculateDiscountAmount = (originalPrice: number, discountValue: number) => {
	return (originalPrice - (originalPrice * discountValue)).toFixed(2)
}

export const getOfferValue = ({ type, discountValue, cartTotal, maxAmount, currencySymbol = getCurrencySymbol() }: { type: overAllDiscount["discountType"]["discountName"], discountValue: string, cartTotal?: number, maxAmount?: string, currencySymbol?: string }) => {
	const formatAmount = (value: string | number | undefined) => CommonService.currencyStandard(currencySymbol, String(value ?? 0));
	const absCart = cartTotal !== undefined ? Math.abs(cartTotal) : undefined;
	if (type == "Fixed") {
		const finalAmount = absCart !== undefined ? Math.max(Math.round((absCart - Number(discountValue)) * 100) / 100, 0) : undefined;
		return {
			offerValue: `${formatAmount(discountValue)} OFF`,
			offerDescription: `Save ${formatAmount(discountValue)} instantly! Use this coupon${absCart !== undefined ? ` and pay only ${formatAmount(finalAmount)} at checkout` : "."}`
		}
	} else {
		let finalAmount: number | undefined = undefined;
		if (absCart !== undefined) {
			const calculatedDiscount = (absCart * Number(discountValue)) / 100;
			const actualDiscount = maxAmount ? Math.min(calculatedDiscount, Number(maxAmount)) : calculatedDiscount;
			finalAmount = Math.max(Math.round((absCart - actualDiscount) * 100) / 100, 0);
		}
		return {
			offerValue: `${discountValue}% OFF`,
			offerDescription: maxAmount ?
				`Flat ${discountValue}% OFF up to ${formatAmount(maxAmount)}! Use this coupon${absCart !== undefined ? ` and pay just ${formatAmount(finalAmount)} after discount` : "."}` :
				`Flat ${discountValue}% OFF with this coupon${absCart !== undefined ? ` and pay just ${formatAmount(finalAmount)} after discount` : "."}`
		}
	}
}

export const getAutomaticOfferValue = ({
	type,
	discountValue,
	cartTotal,
	maxAmount,
	currencySymbol = getCurrencySymbol()
}: {
	type: overAllDiscount["discountType"]["discountName"]
	discountValue: string
	cartTotal?: number
	maxAmount?: string
	currencySymbol?: string
}) => {
	const formatAmount = (value: number | undefined) => CommonService.currencyStandard(currencySymbol, String(value ?? 0))
	const discount = Number(discountValue)
	const maxDiscount = maxAmount ? Number(maxAmount) : undefined

	// helper to prevent negative totals
	const getFinalAmount = (total: number, discountAmt: number) =>
		Math.max(total - discountAmt, 0)

	if (type === "Fixed") {
		const finalAmount =
			cartTotal !== undefined
				? getFinalAmount(cartTotal, discount)
				: undefined

		return {
			offerValue: `${formatAmount(discount)} OFF`,
			offerDescription: cartTotal !== undefined
				? `${formatAmount(discount)} off automatically at checkout — pay only ${formatAmount(finalAmount)}`
				: `${formatAmount(discount)} off automatically at checkout`
		}
	}

	// Percentage discount
	let discountAmount: number | undefined

	if (cartTotal !== undefined) {
		const calculatedDiscount = (cartTotal * discount) / 100
		discountAmount = maxDiscount
			? Math.min(calculatedDiscount, maxDiscount)
			: calculatedDiscount
	}

	const finalAmount =
		cartTotal !== undefined && discountAmount !== undefined
			? getFinalAmount(cartTotal, discountAmount)
			: undefined

	return {
		offerValue: `${discount}% OFF`,
		offerDescription: maxDiscount
			? cartTotal !== undefined
				? `${discount}% off automatically (up to ${formatAmount(maxDiscount)}) — pay just ${formatAmount(finalAmount)}`
				: `${discount}% off automatically, up to ${formatAmount(maxDiscount)}`
			: cartTotal !== undefined
				? `${discount}% off automatically at checkout — pay just ${formatAmount(finalAmount)}`
				: `${discount}% off automatically at checkout`
	}
}

export const remainingState = (state: initialStateType) => {
	return {
		getAllEligibleDiscount: [...state.getAllEligibleDiscount],
		productsDetailsById: { ...state.productsDetailsById },
		discountStore: { ...state.discountStore },
		roundOffSale: state.roundOffSale
	}
}

// Every variant of a product, whether it is a plain product or an option-based one
export const getProductVariants = (productData: productDataType): variantCombinationDetailsType[] => [
	...(productData?.variantCombinationDetails ?? []),
	...(productData?.productOptionMappings?.[0]?.option?.optionValues ?? [])
		.map((option) => option?.productVariantMappings?.[0]?.variantCombinationDetail)
		.filter(Boolean),
]

// The one place a cart line is built from product details, so brand, categories and taxes are always present
export const buildCartLine = (productData: productDataType, variant: variantCombinationDetailsType, quantity: number): addCartProductType => {
	const { brandId, categoryIds, taxes } = normaliseProductAttributes(productData)
	return {
		name: productData?.name,
		variantName: productData?.isVariants && variant?.variantName !== productData?.name ? variant?.variantName || "" : "",
		price: variant?.price,
		quantity,
		variantId: variant?.id,
		productId: productData?.id,
		weightCount: variant?.weightCount,
		stock: variant?.multiLocationVariantStocks?.[0]?.stock,
		finalPrice: variant?.price * quantity,
		sku: variant?.sku,
		upc: variant?.upc,

		discount: 0,
		autoDiscount: 0,
		tax: 0,
		isVariants: productData?.isVariants,
		brandId,
		categoryIds,
		taxes,

		wholeSalePrice: variant?.wholeSalePrice || null,
		weight: null,
		weightUnitId: productData?.weightUnitId || null,
		weightUnit: productData?.weightUnit || null,
		featuredImage: productData?.featuredImage || null,
		multiLocationVariantStocksId: variant?.multiLocationVariantStocks?.[0]?.id,
		variantSkuId: variant?.multiLocationVariantStocks?.[0]?.variantSkuId,
	}
}

const hasStockFor = (variant: variantCombinationDetailsType, quantity: number) => {
	const stock = variant?.multiLocationVariantStocks?.[0]?.stock
	return stock === -1 || (stock ?? 0) >= quantity
}

// Adds one specific variant (used for Buy X Get Y get items). Resolves false when it could not be added.
export const addVariantToCart = async ({ dispatch, locationId, productId, variantId, quantity }: { dispatch: AppDispatch, locationId: number, productId: number, variantId: number, quantity: number }) => {
	try {
		const productDetailsRes = await dispatch(getProductDetailsById({ storeId: config?.storeId, productId, locationId })).unwrap()
		const variant = getProductVariants(productDetailsRes?.productData).find((item) => item?.id === variantId)
		if (!variant) return false
		if (!hasStockFor(variant, quantity)) {
			ZenToast({ type: "warning", message: "Stock is not available" })
			return false
		}
		await dispatch(ensureVariantDiscounts({ productId, variantId, locationId })).unwrap().catch(() => undefined)
		dispatch(addCartProduct({ product: buildCartLine(productDetailsRes?.productData, variant, quantity), locationId }))
		return true
	} catch (err) {
		console.log("add variant error", err)
		return false
	}
}

export const addProductApi = ({ dispatch, locationId, productId, onPressDetails, isFromBarcode = false, skuId = "" }: { dispatch: AppDispatch, locationId: number, productId: number, onPressDetails?: (productId: number) => void, isFromBarcode: boolean, skuId?: string }) => {

	let isVariant = false

	dispatch(getProductDetailsById({ storeId: config?.storeId, productId, locationId, isFromBarcode, skuId })).unwrap().then((productDetailsRes) => {
		const productData = productDetailsRes?.productData
		const variant = isFromBarcode
			? getProductVariants(productData).find((singleVariant) => singleVariant?.sku === skuId || singleVariant?.upc === skuId)!
			: productData?.variantCombinationDetails[0]

		if (!productData?.isVariants || isFromBarcode) {
			if (hasStockFor(variant, variant?.weightCount)) {
				// Cached per variant: a product already in the cart is not refetched
				dispatch(ensureVariantDiscounts({ productId: productData?.id, variantId: variant?.id, locationId })).unwrap().catch((err) => {
					console.log("discount error", err)
				}).finally(() => {
					dispatch(addCartProduct({ product: buildCartLine(productData, variant, variant?.weightCount), locationId }))
				})
			} else {
				ZenToast({ type: "warning", message: "Stock is not available" })
			}
		} else if (!isFromBarcode) {
			onPressDetails && onPressDetails(productId)
		}
	}).catch((err) => {
		console.log("product details error", err)
	})

	return isVariant
}
